import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import {
  BarChart3,
  BedDouble,
  Monitor,
  Moon,
  Sun,
  Bot,
  CalendarDays,
  ClipboardList,
  Flag,
  Inbox,
  LogOut,
  MapPin,
  Megaphone,
  Radio,
  Settings2,
  Sparkles,
  Landmark,
  Mic,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { accountSecurity } from '../lib/cognito';
import { useDragScroll } from '../lib/drag-scroll';
import { useSession } from '../lib/session';
import { useTheme } from '../lib/theme';

const nav = [
  { to: '/events', label: 'Eventi', icon: CalendarDays },
  { to: '/review', label: 'Coda di revisione', short: 'Coda', icon: Inbox },
  { to: '/reports', label: 'Segnalazioni e richieste', short: 'Segnalazioni', icon: Flag },
  { to: '/sources', label: 'Sorgenti', icon: Radio },
  { to: '/rassegne', label: 'Rassegne', icon: Sparkles },
  { to: '/places', label: 'Luoghi', icon: MapPin },
  { to: '/social', label: 'Social', icon: Megaphone },
  { to: '/stats', label: 'Statistiche', icon: BarChart3 },
  { to: '/booking', label: 'Artisti', icon: Mic },
  { to: '/bandi', label: 'Bandi', icon: Landmark },
  { to: '/guests', label: 'Strutture', icon: BedDouble, admin: true },
  { to: '/surveys', label: 'Sondaggi', icon: ClipboardList, admin: true },
  { to: '/users', label: 'Utenti', icon: UsersRound, admin: true },
  { to: '/telegram', label: 'Bot Telegram', short: 'Telegram', icon: Bot, admin: true },
  { to: '/settings', label: 'Impostazioni', short: 'Impostaz.', icon: Settings2, admin: true },
];

/** Intestazione con navigazione e area di lavoro. */
export function Shell() {
  const { config, me, logout, canEdit, isAdmin, accessToken } = useSession();
  const items = nav.filter((n) => !('admin' in n) || isAdmin);
  const { theme, next: nextTheme } = useTheme();
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const themeLabel =
    theme === 'dark' ? 'Tema scuro' : theme === 'light' ? 'Tema chiaro' : 'Tema come il sistema';
  // sul telefono la sezione aperta resta visibile nella riga che scorre
  const location = useLocation();
  const mobileNav = useRef<HTMLElement>(null);
  useDragScroll(mobileNav);
  useEffect(() => {
    mobileNav.current
      ?.querySelector<HTMLElement>('a[aria-current="page"]')
      ?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [location.pathname, isAdmin]);
  // amministratori senza secondo fattore: promemoria in cima a ogni pagina
  const [noTotp, setNoTotp] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    accessToken()
      .then((t) => accountSecurity(config, t))
      .then(
        (s) => setNoTotp(!s.totp),
        () => undefined,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  return (
    <div className="min-h-dvh bg-page text-fg">
      <header className="sticky top-0 z-30 border-b border-surface-strong bg-chrome">
        <div className="flex h-14 items-center gap-3 px-4 md:gap-6">
          <NavLink to="/events" className="flex shrink-0 items-center gap-2">
            <img
              src="/logo.svg"
              alt="Salento Sagre Eventi"
              className="h-6 w-auto md:h-7 dark:invert"
            />
            <span className="rounded-none bg-fg px-1.5 text-[11px] font-medium uppercase text-page">
              {config.env}
            </span>
          </NavLink>
          {/* da tablet nella stessa riga: solo icone (col nome al passaggio del mouse) sotto i 1800 px,
              poi le etichette brevi */}
          <nav
            className="hidden min-w-0 items-center gap-1 overflow-x-auto md:flex"
            aria-label="Sezioni"
          >
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                title={n.label}
                className={({ isActive }) =>
                  `flex h-10 shrink-0 items-center gap-2 whitespace-nowrap px-3 text-sm min-[1800px]:px-2 ${isActive ? 'bg-page font-medium' : 'hover:bg-surface-strong'}`
                }
              >
                <n.icon className="size-4" aria-hidden />
                <span className="hidden min-[1800px]:inline">
                  {'short' in n ? n.short : n.label}
                </span>
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1 text-sm md:gap-3">
            <button
              type="button"
              onClick={nextTheme}
              title={`${themeLabel}: tocca per cambiare`}
              aria-label={`${themeLabel}: cambia tema`}
              className="flex h-10 items-center px-2 text-muted hover:bg-surface-strong"
            >
              <ThemeIcon className="size-4" aria-hidden />
            </button>
            <NavLink
              to="/account"
              title="Il mio account: password e autenticazione a due fattori"
              aria-label="Il mio account"
              className={({ isActive }) =>
                `flex h-10 items-center gap-2 whitespace-nowrap px-2 ${isActive ? 'bg-page font-medium' : 'text-muted hover:bg-surface-strong'}`
              }
            >
              <UserRound className="size-4" aria-hidden />
              <span className="hidden min-[1920px]:inline">
                {me?.email ?? '…'}
                {me && ` · ${me.groups.join(', ') || 'nessun ruolo'}`}
              </span>
            </NavLink>
            <button
              onClick={logout}
              title={me?.email ? `${me.email} · ${me.groups.join(', ')}` : 'Esci'}
              aria-label="Esci"
              className="flex h-10 items-center gap-1 whitespace-nowrap px-2 hover:bg-surface-strong"
            >
              <LogOut className="size-4" aria-hidden />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
        {/* telefono: le sezioni in una riga sotto, con icona ed etichetta, che scorre in orizzontale */}
        <nav
          ref={mobileNav}
          className="flex cursor-grab gap-1 overflow-x-auto border-t border-surface-strong px-2 py-1 select-none [scrollbar-width:none] active:cursor-grabbing md:hidden"
          aria-label="Sezioni"
        >
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `flex shrink-0 flex-col items-center gap-0.5 px-3 py-1.5 text-[11px] ${isActive ? 'bg-page font-medium' : 'text-muted'}`
              }
            >
              <n.icon className="size-5" aria-hidden />
              {'short' in n ? n.short : n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      {noTotp && (
        <p role="status" className="bg-accent px-4 py-2 text-sm">
          Sei un amministratore e non hai l’autenticazione a due fattori:{' '}
          <Link to="/account" onClick={() => setNoTotp(false)} className="font-medium underline">
            attivala dal tuo account
          </Link>
          .
        </p>
      )}
      {me && !canEdit && (
        <p role="alert" className="bg-danger-soft px-4 py-2 text-sm">
          Il tuo utente non ha il ruolo <strong>editor</strong> o <strong>admin</strong>: puoi
          entrare ma non gestire gli eventi. Chiedi a un amministratore di aggiungerti al gruppo in
          Cognito.
        </p>
      )}
      <Outlet />
    </div>
  );
}
